import {
    ArrowBack as ArrowBackIcon,
    Business as BusinessIcon,
    Email as EmailIcon,
    Wc as GenderIcon,
    Person as PersonIcon,
    Phone as PhoneIcon,
    PersonPin as ReferralIcon,
    Send as SendIcon
} from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Container,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Grid,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import axios from 'axios';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

const Enquiry = () => {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const location = useLocation();
    const apiURL = process.env.REACT_APP_API;

    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [franchises, setFranchises] = useState([]);
    const [sendingOtp, setSendingOtp] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [otpTimer, setOtpTimer] = useState(0);

    // Get course ID from URL
    const getCourseId = () => {
        const params = new URLSearchParams(location.search);
        return params.get('courseid');
    };

    // Referral sources dropdown options
    const referralSources = [
        { value: 'friend', label: 'Friend/Family' },
        { value: 'social_media', label: 'Social Media' },
        { value: 'website', label: 'Website' },
        { value: 'search_engine', label: 'Search Engine' },
        { value: 'newspaper', label: 'Newspaper/Advertisement' },
        { value: 'seminar', label: 'Seminar/Workshop' },
        { value: 'alumni', label: 'College Alumni' },
        { value: 'other', label: 'Other' },
    ];

    // Yup validation schema
    const validationSchema = Yup.object({
        enq_student_name: Yup.string()
            .min(2, 'Name must be at least 2 characters')
            .required('Name is required'),
        enq_student_gender: Yup.string()
            .oneOf(['male', 'female', 'other'], 'Invalid gender')
            .required('Gender is required'),
        enq_student_mobile: Yup.string()
            .matches(/^\d{10}$/, 'Enter valid 10-digit mobile number')
            .required('Mobile number is required'),
        enq_student_email: Yup.string()
            .email('Enter valid email address')
            .required('Email is required'),
        enq_student_from: Yup.string()
            .required('Please select referral source'),
        franchiseid: Yup.string()
            .required('Please select a franchise')
    });

    // Formik initialization
    const formik = useFormik({
        initialValues: {
            enq_student_name: '',
            enq_student_gender: 'male',
            enq_student_mobile: '',
            enq_student_email: '',
            enq_student_from: '',
            franchiseid: '',
            otp: ''
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            try {
                const courseId = getCourseId();
                const payload = {
                    franchiseid: values.franchiseid,
                    courseid: courseId,
                    enq_student_name: values.enq_student_name,
                    enq_student_gender: values.enq_student_gender,
                    enq_student_mobile: values.enq_student_mobile,
                    enq_student_email: values.enq_student_email,
                    enq_student_from: values.enq_student_from,
                    enq_course_amount_max: course?.c_max_price || 0,
                    enq_coures_amount: course?.c_price || 0,
                    enq_status: 'pending',
                    otp: values.otp || ''
                };

                const response = await axios.post(`${apiURL}/public/enquiry/index`, payload);

                if (response.data.status === 'success' || response.data.status === true) {
                    toast.success(response.data.message || 'Enquiry submitted successfully!');
                    enqueueSnackbar('Our team will contact you soon.', { variant: 'success' });

                    // Reset form and OTP states
                    formik.resetForm();
                    setOtpSent(false);
                    setOtpTimer(0);

                    navigate(`/courses/receipt?enquiryid=${response.data.data.enquiryid}`)

                } else {
                    toast.error(response.data.message || 'Failed to submit enquiry');
                }
            } catch (error) {
                console.error('Error submitting enquiry:', error);
                toast.error(error.response?.data?.message || 'An error occurred while submitting enquiry');
            }
        }
    });

    // Fetch course details
    useEffect(() => {
        const fetchCourseDetails = async () => {
            const courseId = getCourseId();
            if (!courseId) {
                toast.error('No course ID provided');
                navigate('/courses');
                return;
            }

            setLoading(true);
            try {
                const response = await axios.get(`${apiURL}/public/course/details`, {
                    params: { courseid: courseId }
                });

                if (response.data.status === 'success' || response.data.status === true) {
                    setCourse(response.data.data);
                } else {
                    toast.error(response.data.message || 'Failed to fetch course details');
                    navigate('/courses');
                }
            } catch (error) {
                console.error('Error fetching course details:', error);
                toast.error(error.response?.data?.message || 'An error occurred while fetching course details');
                navigate('/courses');
            } finally {
                setLoading(false);
            }
        };

        fetchCourseDetails();
    }, [apiURL, navigate]);

    // Fetch franchises
    useEffect(() => {
        const fetchFranchises = async () => {
            try {
                const response = await axios.get(`${apiURL}/public/franchise/getall`);

                if (response.data.status === 'success' || response.data.status === true) {
                    setFranchises(response.data.data || []);
                } else {
                    toast.error(response.data.message || 'Failed to fetch franchises');
                    setFranchises([]);
                }
            } catch (error) {
                console.error('Error fetching franchises:', error);
                toast.error('Failed to load franchises');
                setFranchises([]);
            }
        };

        fetchFranchises();
    }, [apiURL]);

    const handleBack = () => {
        navigate(-1);
    };

    const handleSendOtp = async () => {
        if (!formik.values.enq_student_email) {
            formik.setFieldError('enq_student_email', 'Email is required to send OTP');
            return;
        }

        if (formik.errors.enq_student_email) {
            return;
        }

        setSendingOtp(true);
        try {
            let formData = new FormData();
            formData.append('email', formik.values.enq_student_email);
            const response = await axios.post(`${apiURL}/class/send-otp`, formData);

            if (response.data.status === 'success' || response.data.status === true) {
                setOtpSent(true);
                setOtpTimer(60);

                // Start 60-second countdown timer
                const timer = setInterval(() => {
                    setOtpTimer(prev => {
                        if (prev <= 1) {
                            clearInterval(timer);
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);

                toast.success(response.data.message || `OTP sent to ${formik.values.enq_student_email}`);
                enqueueSnackbar('OTP sent successfully! Check your email.', { variant: 'success' });
            } else {
                toast.error(response.data.message || 'Failed to send OTP');
            }
        } catch (error) {
            console.error('Error sending OTP:', error);
            toast.error(error.response?.data?.message || 'Failed to send OTP');
        } finally {
            setSendingOtp(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Stack spacing={2} alignItems="center">
                    <CircularProgress size={48} />
                    <Typography variant="body1" color="text.secondary">
                        Loading course details...
                    </Typography>
                </Stack>
            </Box>
        );
    }

    if (!course) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Card sx={{ p: 3, maxWidth: 500, textAlign: 'center' }}>
                    <Typography variant="h6" color="error" gutterBottom>
                        Course Not Found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Please check the URL and try again.
                    </Typography>
                </Card>
            </Box>
        );
    }

    return (
        <Box sx={{ py: 4 }}>
            <Container maxWidth="lg">
                {/* Back Button */}
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                    sx={{ mb: 3 }}
                >
                    Back
                </Button>

                <Grid container spacing={4}>
                    {/* Left Column - Course Details */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ p: 3, borderRadius: 2 }}>
                            <Typography variant="h5" fontWeight={600} gutterBottom>
                                Course Details
                            </Typography>
                            <Divider sx={{ my: 2 }} />

                            <Stack spacing={3}>
                                {/* Course Title */}
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Course Name
                                    </Typography>
                                    <Typography variant="h6" fontWeight={600}>
                                        {course.c_title}
                                    </Typography>
                                    <Typography variant="caption" color="primary">
                                        {course.c_name}
                                    </Typography>
                                </Box>

                                {/* Description */}
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Description
                                    </Typography>
                                    <Typography variant="body2">
                                        {course.c_description}
                                    </Typography>
                                </Box>

                                {/* Duration */}
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Duration
                                    </Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                        {course.c_duration} months
                                    </Typography>
                                </Box>

                                {/* Pricing */}
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Course Fee
                                    </Typography>
                                    <Stack direction="row" alignItems="baseline" spacing={1}>
                                        <Typography variant="h5" fontWeight={700} color="primary.main">
                                            {formatCurrency(course.c_price)}
                                        </Typography>
                                        {course.c_price < course.c_max_price && (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ textDecoration: 'line-through' }}
                                            >
                                                {formatCurrency(course.c_max_price)}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Box>

                                {/* Status */}
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Status
                                    </Typography>
                                    <Chip
                                        label={course.c_status === 'active' ? 'Available' : 'Not Available'}
                                        color={course.c_status === 'active' ? 'success' : 'error'}
                                        size="small"
                                    />
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>

                    {/* Right Column - Enquiry Form */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ p: 3, borderRadius: 2 }}>
                            <Typography variant="h5" fontWeight={600} gutterBottom>
                                Student Enquiry Form
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Fill your details to get more information about this course
                            </Typography>

                            <Divider sx={{ mb: 3 }} />

                            <Box component="form" onSubmit={formik.handleSubmit} noValidate>
                                <Stack spacing={3}>
                                    {/* Student Name */}
                                    <TextField
                                        required
                                        fullWidth
                                        label="Full Name"
                                        name="enq_student_name"
                                        value={formik.values.enq_student_name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.enq_student_name && Boolean(formik.errors.enq_student_name)}
                                        helperText={formik.touched.enq_student_name && formik.errors.enq_student_name}
                                        placeholder="Enter your full name"
                                        InputProps={{
                                            startAdornment: <PersonIcon color="action" sx={{ mr: 1 }} />
                                        }}
                                    />

                                    {/* Gender */}
                                    <FormControl component="fieldset">
                                        <FormLabel component="legend" sx={{ mb: 1 }}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <GenderIcon fontSize="small" color="action" />
                                                <span>Gender</span>
                                            </Stack>
                                        </FormLabel>
                                        <RadioGroup
                                            row
                                            name="enq_student_gender"
                                            value={formik.values.enq_student_gender}
                                            onChange={formik.handleChange}
                                        >
                                            <FormControlLabel value="male" control={<Radio />} label="Male" />
                                            <FormControlLabel value="female" control={<Radio />} label="Female" />
                                            <FormControlLabel value="other" control={<Radio />} label="Other" />
                                        </RadioGroup>
                                    </FormControl>

                                    {/* Mobile Number */}
                                    <TextField
                                        required
                                        fullWidth
                                        label="Mobile Number"
                                        name="enq_student_mobile"
                                        value={formik.values.enq_student_mobile}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.enq_student_mobile && Boolean(formik.errors.enq_student_mobile)}
                                        helperText={formik.touched.enq_student_mobile && formik.errors.enq_student_mobile}
                                        placeholder="Enter 10-digit mobile number"
                                        InputProps={{
                                            startAdornment: <PhoneIcon color="action" sx={{ mr: 1 }} />,
                                            inputProps: { maxLength: 10 }
                                        }}
                                    />

                                    {/* Email with Send OTP */}
                                    <Box>
                                        <Stack direction="row" spacing={1} alignItems="flex-start">
                                            <TextField
                                                required
                                                fullWidth
                                                label="Email Address"
                                                name="enq_student_email"
                                                type="email"
                                                value={formik.values.enq_student_email}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.enq_student_email && Boolean(formik.errors.enq_student_email)}
                                                helperText={formik.touched.enq_student_email && formik.errors.enq_student_email}
                                                placeholder="Enter your email"
                                                InputProps={{
                                                    startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />
                                                }}
                                            />
                                            <Button
                                                variant="outlined"
                                                onClick={handleSendOtp}
                                                disabled={!formik.values.enq_student_email || sendingOtp || otpTimer > 0}
                                                sx={{ minWidth: 120, height: 56 }}
                                            >
                                                {sendingOtp ? (
                                                    <CircularProgress size={20} />
                                                ) : otpTimer > 0 ? (
                                                    `${otpTimer}s`
                                                ) : (
                                                    'Send OTP'
                                                )}
                                            </Button>
                                        </Stack>

                                        {/* OTP Input Field - Shows after OTP is sent */}
                                        {otpSent && (
                                            <TextField
                                                fullWidth
                                                label="Enter OTP"
                                                name="otp"
                                                value={formik.values.otp}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.otp && Boolean(formik.errors.otp)}
                                                helperText={formik.touched.otp && formik.errors.otp || 'Enter 6-digit OTP sent to your email'}
                                                placeholder="123456"
                                                sx={{ mt: 2 }}
                                                InputProps={{
                                                    inputProps: { maxLength: 6 }
                                                }}
                                            />
                                        )}
                                    </Box>

                                    {/* Franchise Selection */}
                                    <FormControl
                                        fullWidth
                                        required
                                        error={formik.touched.franchiseid && Boolean(formik.errors.franchiseid)}
                                    >
                                        <InputLabel>Select Franchise</InputLabel>
                                        <Select
                                            name="franchiseid"
                                            value={formik.values.franchiseid}
                                            label="Select Franchise"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            startAdornment={
                                                <BusinessIcon color="action" sx={{ mr: 1 }} />
                                            }
                                        >
                                            {franchises.map((franchise) => (
                                                <MenuItem key={franchise.franchiseid} value={franchise.franchiseid}>
                                                    {franchise.fr_name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {formik.touched.franchiseid && formik.errors.franchiseid && (
                                            <FormHelperText error>
                                                {formik.errors.franchiseid}
                                            </FormHelperText>
                                        )}
                                    </FormControl>

                                    {/* Referral Source Dropdown */}
                                    <FormControl
                                        fullWidth
                                        required
                                        error={formik.touched.enq_student_from && Boolean(formik.errors.enq_student_from)}
                                    >
                                        <InputLabel>How did you hear about us?</InputLabel>
                                        <Select
                                            name="enq_student_from"
                                            value={formik.values.enq_student_from}
                                            label="How did you hear about us?"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            startAdornment={
                                                <ReferralIcon color="action" sx={{ mr: 1 }} />
                                            }
                                        >
                                            {referralSources.map((source) => (
                                                <MenuItem key={source.value} value={source.value}>
                                                    {source.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {formik.touched.enq_student_from && formik.errors.enq_student_from && (
                                            <FormHelperText error>
                                                {formik.errors.enq_student_from}
                                            </FormHelperText>
                                        )}
                                    </FormControl>

                                    {/* Submit Button */}
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size="large"
                                        fullWidth
                                        startIcon={<SendIcon />}
                                        disabled={formik.isSubmitting}
                                        sx={{ py: 1.5, borderRadius: 2, fontWeight: 600 }}
                                    >
                                        {formik.isSubmitting ? 'Submitting...' : 'Submit Enquiry'}
                                    </Button>

                                    {/* Terms Note */}
                                    <Typography variant="caption" color="text.secondary" align="center">
                                        By submitting this form, you agree to receive communication from us regarding this course.
                                    </Typography>
                                </Stack>
                            </Box>
                        </Card>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default Enquiry;